import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  fetchReleases,
  fetchActiveReleases,
  fetchArchivedReleases,
  fetchReleaseById,
  createRelease,
  updateRelease,
  archiveRelease,
  unarchiveRelease,
  deleteRelease,
  cloneRelease,
  getCloneJobStatus
} from '../../api/releaseApi'

// ============================================================================
// ASYNC THUNKS
// ============================================================================

/**
 * FETCH ALL RELEASES
 * Fetches all releases (active and archived) for a project
 */
export const fetchReleasesAsync = createAsyncThunk(
  'releases/fetchReleases',
  async (jiraProjectId, { rejectWithValue }) => {
    try {
      return await fetchReleases(jiraProjectId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch releases',
        status: error.response?.status
      })
    }
  }
)

/**
 * FETCH ACTIVE RELEASES
 * Fetches only non-archived releases for a project
 */
export const fetchActiveReleasesAsync = createAsyncThunk(
  'releases/fetchActiveReleases',
  async (jiraProjectId, { rejectWithValue }) => {
    try {
      return await fetchActiveReleases(jiraProjectId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch active releases',
        status: error.response?.status
      })
    }
  }
)

/**
 * FETCH ARCHIVED RELEASES
 * Fetches only archived releases for a project
 */
export const fetchArchivedReleasesAsync = createAsyncThunk(
  'releases/fetchArchivedReleases',
  async (jiraProjectId, { rejectWithValue }) => {
    try {
      return await fetchArchivedReleases(jiraProjectId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch archived releases',
        status: error.response?.status
      })
    }
  }
)

/**
 * FETCH SINGLE RELEASE
 * Fetches a release by ID with full details
 */
export const fetchReleaseByIdAsync = createAsyncThunk(
  'releases/fetchReleaseById',
  async (releaseId, { rejectWithValue }) => {
    try {
      return await fetchReleaseById(releaseId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch release',
        status: error.response?.status
      })
    }
  }
)

/**
 * CREATE RELEASE
 * Creates a new release for a project
 */
export const createReleaseAsync = createAsyncThunk(
  'releases/createRelease',
  async (releaseData, { rejectWithValue }) => {
    try {
      return await createRelease(releaseData)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to create release',
        status: error.response?.status
      })
    }
  }
)

/**
 * UPDATE RELEASE
 * Updates an existing release
 */
export const updateReleaseAsync = createAsyncThunk(
  'releases/updateRelease',
  async ({ releaseId, updates }, { rejectWithValue }) => {
    try {
      const data = await updateRelease(releaseId, updates)
      return { releaseId, data }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to update release',
        status: error.response?.status
      })
    }
  }
)

/**
 * ARCHIVE RELEASE
 * Archives a release (moves to archived section)
 */
export const archiveReleaseAsync = createAsyncThunk(
  'releases/archiveRelease',
  async (releaseId, { rejectWithValue }) => {
    try {
      await archiveRelease(releaseId)
      return { releaseId }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to archive release',
        status: error.response?.status
      })
    }
  }
)

/**
 * UNARCHIVE RELEASE
 * Unarchives a release (moves back to active section)
 */
export const unarchiveReleaseAsync = createAsyncThunk(
  'releases/unarchiveRelease',
  async (releaseId, { rejectWithValue }) => {
    try {
      await unarchiveRelease(releaseId)
      return { releaseId }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to unarchive release',
        status: error.response?.status
      })
    }
  }
)

/**
 * DELETE RELEASE
 * Permanently deletes a release and all its contents
 */
export const deleteReleaseAsync = createAsyncThunk(
  'releases/deleteRelease',
  async (releaseId, { rejectWithValue }) => {
    try {
      await deleteRelease(releaseId)
      return { releaseId }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to delete release',
        status: error.response?.status
      })
    }
  }
)

/**
 * START CLONE RELEASE (async)
 * Initiates a clone job and returns the job ID for polling.
 * Accepts either a releaseId (number) or { releaseId, options } object.
 */
export const cloneReleaseAsync = createAsyncThunk(
  'releases/cloneRelease',
  async (payload, { rejectWithValue }) => {
    try {
      const releaseId = typeof payload === 'object' ? payload.releaseId : payload
      const options = typeof payload === 'object' ? payload.options : null
      // Returns { jobId, status: "IN_PROGRESS" }
      const jobResponse = await cloneRelease(releaseId, options)
      return { ...jobResponse, sourceReleaseId: releaseId }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to start clone',
        status: error.response?.status
      })
    }
  }
)

/**
 * POLL CLONE JOB STATUS
 * Checks the status of an ongoing clone job.
 * Returns { jobId, status, clonedRelease?, errorMessage? }
 */
export const pollCloneStatusAsync = createAsyncThunk(
  'releases/pollCloneStatus',
  async (jobId, { rejectWithValue }) => {
    try {
      return await getCloneJobStatus(jobId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to check clone status',
        status: error.response?.status
      })
    }
  }
)

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState = {
  // List of all releases for the current project
  releases: [],

  // Currently selected/viewed release (for folder browser)
  currentRelease: null,

  // Loading states
  loading: false,
  currentReleaseLoading: false,

  // Error state
  error: null,

  // Tracks release IDs currently being cloned (for UI indicators)
  // { [sourceReleaseId]: jobId }
  cloningReleases: {}
}

// ============================================================================
// SLICE
// ============================================================================

const releaseSlice = createSlice({
  name: 'releases',
  initialState,
  reducers: {
    /**
     * Clear any error in state
     */
    clearReleasesError: (state) => {
      state.error = null
    },

    /**
     * Clear current release when navigating away
     */
    clearCurrentRelease: (state) => {
      state.currentRelease = null
    },

    /**
     * Set current release directly (for optimistic updates)
     */
    setCurrentRelease: (state, action) => {
      state.currentRelease = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      // ====================================================================
      // FETCH RELEASES (all)
      // ====================================================================
      .addCase(fetchReleasesAsync.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchReleasesAsync.fulfilled, (state, action) => {
        state.loading = false
        state.releases = action.payload || []
        state.error = null
      })
      .addCase(fetchReleasesAsync.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to fetch releases'
      })

      // ====================================================================
      // FETCH ACTIVE RELEASES
      // ====================================================================
      .addCase(fetchActiveReleasesAsync.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchActiveReleasesAsync.fulfilled, (state, action) => {
        state.loading = false
        // Replace releases with only active ones (filter out archived if any exist)
        state.releases = action.payload || []
        state.error = null
      })
      .addCase(fetchActiveReleasesAsync.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to fetch active releases'
      })

      // ====================================================================
      // FETCH ARCHIVED RELEASES
      // ====================================================================
      .addCase(fetchArchivedReleasesAsync.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchArchivedReleasesAsync.fulfilled, (state, action) => {
        state.loading = false
        // Append archived releases to existing active ones
        const archivedReleases = action.payload || []
        // Merge without duplicates
        const existingIds = new Set(state.releases.map(r => r.id))
        const newReleases = archivedReleases.filter(r => !existingIds.has(r.id))
        state.releases = [...state.releases, ...newReleases]
        state.error = null
      })
      .addCase(fetchArchivedReleasesAsync.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to fetch archived releases'
      })

      // ====================================================================
      // FETCH SINGLE RELEASE
      // ====================================================================
      .addCase(fetchReleaseByIdAsync.pending, (state) => {
        state.currentReleaseLoading = true
        state.error = null
      })
      .addCase(fetchReleaseByIdAsync.fulfilled, (state, action) => {
        state.currentReleaseLoading = false
        state.currentRelease = action.payload
        state.error = null
      })
      .addCase(fetchReleaseByIdAsync.rejected, (state, action) => {
        state.currentReleaseLoading = false
        state.error = action.payload?.message || 'Failed to fetch release'
      })

      // ====================================================================
      // CREATE RELEASE
      // ====================================================================
      .addCase(createReleaseAsync.fulfilled, (state, action) => {
        // Add the new release to the beginning of the list (shows at top)
        state.releases.unshift(action.payload)
      })
      .addCase(createReleaseAsync.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to create release'
      })

      // ====================================================================
      // UPDATE RELEASE
      // ====================================================================
      .addCase(updateReleaseAsync.fulfilled, (state, action) => {
        const { releaseId, data } = action.payload
        const index = state.releases.findIndex(r => r.id === releaseId)
        if (index !== -1) {
          state.releases[index] = { ...state.releases[index], ...data }
        }
        // Also update current release if it's the one being updated
        if (state.currentRelease?.id === releaseId) {
          state.currentRelease = { ...state.currentRelease, ...data }
        }
      })
      .addCase(updateReleaseAsync.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to update release'
      })

      // ====================================================================
      // ARCHIVE RELEASE
      // ====================================================================
      .addCase(archiveReleaseAsync.fulfilled, (state, action) => {
        const { releaseId } = action.payload
        const release = state.releases.find(r => r.id === releaseId)
        if (release) {
          release.archived = true
        }
      })
      .addCase(archiveReleaseAsync.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to archive release'
      })

      // ====================================================================
      // UNARCHIVE RELEASE
      // ====================================================================
      .addCase(unarchiveReleaseAsync.fulfilled, (state, action) => {
        const { releaseId } = action.payload
        const release = state.releases.find(r => r.id === releaseId)
        if (release) {
          release.archived = false
        }
      })
      .addCase(unarchiveReleaseAsync.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to unarchive release'
      })

      // ====================================================================
      // DELETE RELEASE
      // ====================================================================
      .addCase(deleteReleaseAsync.fulfilled, (state, action) => {
        const { releaseId } = action.payload
        state.releases = state.releases.filter(r => r.id !== releaseId)
        // Clear current release if it was deleted
        if (state.currentRelease?.id === releaseId) {
          state.currentRelease = null
        }
      })
      .addCase(deleteReleaseAsync.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to delete release'
      })

      // ====================================================================
      // CLONE RELEASE (async job start)
      // ====================================================================
      .addCase(cloneReleaseAsync.fulfilled, (state, action) => {
        // Track that this release is being cloned
        const { sourceReleaseId, jobId } = action.payload
        state.cloningReleases[sourceReleaseId] = jobId
      })
      .addCase(cloneReleaseAsync.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to start clone'
      })

      // ====================================================================
      // POLL CLONE STATUS
      // ====================================================================
      .addCase(pollCloneStatusAsync.fulfilled, (state, action) => {
        const { status, clonedRelease } = action.payload
        if (status === 'COMPLETED' && clonedRelease) {
          // Add the cloned release to the beginning of the list
          state.releases.unshift(clonedRelease)
          // Remove from cloning tracking
          const sourceId = Object.keys(state.cloningReleases).find(
            key => state.cloningReleases[key] === action.payload.jobId
          )
          if (sourceId) {
            delete state.cloningReleases[sourceId]
          }
        } else if (status === 'FAILED') {
          // Remove from cloning tracking
          const sourceId = Object.keys(state.cloningReleases).find(
            key => state.cloningReleases[key] === action.payload.jobId
          )
          if (sourceId) {
            delete state.cloningReleases[sourceId]
          }
        }
      })
  }
})

// ============================================================================
// ACTIONS
// ============================================================================

export const {
  clearReleasesError,
  clearCurrentRelease,
  setCurrentRelease
} = releaseSlice.actions

// ============================================================================
// SELECTORS
// ============================================================================

/**
 * Select all releases
 * Returns empty array if state is not initialized
 */
export const selectReleases = (state) => state.releases?.releases || []

/**
 * Select current release (for folder browser)
 */
export const selectCurrentRelease = (state) => state.releases?.currentRelease || null

/**
 * Select loading state
 */
export const selectReleasesLoading = (state) => state.releases?.loading || false

/**
 * Select current release loading state
 */
export const selectCurrentReleaseLoading = (state) => state.releases?.currentReleaseLoading || false

/**
 * Select error state
 */
export const selectReleasesError = (state) => state.releases?.error || null

/**
 * Select active releases only
 */
export const selectActiveReleases = (state) =>
  (state.releases?.releases || []).filter(r => !r.archived)

/**
 * Select archived releases only
 */
export const selectArchivedReleases = (state) =>
  (state.releases?.releases || []).filter(r => r.archived)

/**
 * Select cloning releases map { sourceReleaseId: jobId }
 */
export const selectCloningReleases = (state) =>
  state.releases?.cloningReleases || {}

export default releaseSlice.reducer
